/** Confirm + choose a destination event for a move. */
import { useState } from 'react';
import { IconArrowsMove, IconAlertTriangle } from '@tabler/icons-react';
import { Modal } from '../ui/Modal.tsx';
import { Button } from '../ui/Button.tsx';
import { EventPicker } from '../upload/EventPicker.tsx';
import { Spinner } from '../ui/Spinner.tsx';
import { UNCATEGORIZED_EVENT } from '../../config/awsConfig.ts';

interface MoveToEventDialogProps {
  open: boolean;
  count: number;
  sourceEvent: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: (destinationEvent: string) => void;
}

export function MoveToEventDialog({
  open,
  count,
  sourceEvent,
  busy,
  onClose,
  onConfirm,
}: MoveToEventDialogProps) {
  const [dest, setDest] = useState<string>(UNCATEGORIZED_EVENT);
  const sameEvent = dest === sourceEvent;

  return (
    <Modal open={open} onClose={onClose} title={`Move ${count} item${count !== 1 ? 's' : ''}`}>
      <div className="space-y-4">
        <p className="text-sm text-fg-2">
          Choose a destination event. The photos (and their thumbnails) will be relocated from
          <span className="font-medium text-fg"> {sourceEvent}</span>.
        </p>

        <EventPicker value={dest} onChange={setDest} />

        <div className="flex items-start gap-2 rounded-xl bg-amber-500/10 p-3 text-xs text-amber-600">
          <IconAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Moving removes the photos from their current event for everyone. This can't be undone
            from the app.
          </span>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onConfirm(dest)} disabled={busy || sameEvent}>
            {busy ? <Spinner className="h-4 w-4" /> : <IconArrowsMove className="h-4 w-4" />}
            {sameEvent ? 'Pick a different event' : `Move here`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
